/**
 * What a folder holds -- its subfolders, then its records -- shown the four ways a Mac's Finder offers: as Icons, as a
 * List, in Columns, and as a Gallery.
 *
 * Ed, 2026-11-03: "I would like the options available on a mac for viewing the content area."
 *
 * ONE CLICK OPENS, AS EVERYWHERE ELSE IN THE PRODUCT, not Finder's click-to-select and double-click-to-open: a folder
 * opens, and a record opens in the record drawer (its Object ID) or a new tab (the icon beside it). Columns and
 * Gallery are the two views whose point is a selection, so there a record is selected and previewed in place, with
 * the same two doors on the preview.
 *
 * COLUMNS IS THE PATH, from the root to the open folder, one column a level; the page supplies each column's contents
 * (an ancestor's records are read when this view asks for them), so this component draws and never fetches.
 */
import React, {useEffect, useState} from "react";
import {Box, Button, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip,
        Typography} from "@mui/material";
import {ArticleOutlined, ChevronRightOutlined, DragIndicator, DragIndicatorOutlined, FolderOutlined, GridViewOutlined, InsertDriveFileOutlined,
        LockPersonOutlined, ViewCarouselOutlined, ViewColumnOutlined, ViewListOutlined} from "@mui/icons-material";

import ObjectIdLink from "../Project/ObjectIdLink";
import {classificationByCode} from "../../util/objectClassifications";
import {Blue3, Grey5, Grey6, Grey7, Grey17} from "../../theme/styles";

type Row = Record<string, any>;
export type FolderView = "icons" | "list" | "columns" | "gallery";
export const FOLDER_VIEWS: {view: FolderView; label: string; Icon: React.ElementType}[] = [
    {view: "icons", label: "as Icons", Icon: GridViewOutlined},
    {view: "list", label: "as List", Icon: ViewListOutlined},
    {view: "columns", label: "as Columns", Icon: ViewColumnOutlined},
    {view: "gallery", label: "as Gallery", Icon: ViewCarouselOutlined},
];

/** Every visit opens as a List; a view chosen on the page holds while the reader moves between folders, not across visits. */
export const DEFAULT_VIEW: FolderView = "list";

/** One thing a folder holds, as every view draws it. */
export type Item = {kind: "folder"; folder: Row} | {kind: "record"; record: Row};

/** A STANDALONE PARKOUR DOC (2026-11-04) drawn as a record row: it lives in the folder and belongs to no record, so it
 * has no Object ID, revision or status, and opens in Parkour-AI Docs rather than the record drawer. It is dragged to
 * another folder like a record (Ed: "a doc needs to have a gripper to drag and drop it into a new folder"); the page
 * moves it with folder_docs rather than folder_records. `ObjectUSID` and `RevisionUSID` both carry its own USID: the first keys the row, the second
 * is what `onOpenRecord` hands back, and the page tells a Doc's from a record's. */
export function docRows(docs: Row[] | undefined): Row[] {
    return (docs ?? []).map(doc => ({Doc: true, ObjectUSID: doc.USID, RevisionUSID: doc.USID, Name: doc.Name,
                                     FiledBy: doc.CreatedBy, FiledDatetime: doc.CreatedDatetime}));
}

export function itemsOf(folders: Row[], records: Row[]): Item[] {
    return [...folders.map(folder => ({kind: "folder" as const, folder})), ...records.map(record => ({kind: "record" as const, record}))];
}

function recordIcon(record: Row) {
    if (record.Doc) return ArticleOutlined;
    return classificationByCode(record.Classification)?.icon ?? InsertDriveFileOutlined;
}

function typeName(record: Row) {
    if (record.Doc) return "Parkour Doc";
    return classificationByCode(record.Classification)?.singular ?? record.Classification;
}

export function ViewSwitch({view, onView}: {view: FolderView; onView: (view: FolderView) => void}) {
    return (
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, next) => { if (next) onView(next); }}
                           aria-label="View as">
            {FOLDER_VIEWS.map(({view: one, label, Icon}) => (
                <ToggleButton key={one} value={one} aria-label={`View ${label}`} sx={{px: 1, py: 0.4}}>
                    <Tooltip title={`View ${label}`}><Icon sx={{fontSize: 18}}/></Tooltip>
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
}

/** Dropping onto a folder shown here: a record files into it, a folder moves into it. The page decides what may land
 *  where (the tree's rules) and confirms before anything moves; this only lights the target and reports the drop. */
export type ContentDrop = {
    folderDrag: string | null;
    onFolderDragStart: (folder: Row) => void;
    onDragFinished: () => void;
    canDrop: (folder: Row) => boolean;
    onDrop: (folder: Row) => void;
};

type Common = {
    onOpenFolder: (usid: string) => void;
    onOpenRecord: (revisionUSID: string) => void;
    /** Records may be dragged onto a folder in the tree when the reader may file in this one. */
    mayFile: boolean;
    onRecordDragStart: (record: Row) => void;
    onRecordDragEnd: () => void;
    drop?: ContentDrop;
};

/** A folder shown in the content area: draggable when the reader manages it, and a drop target where the page allows. */
function useFolderTarget(common: Common) {
    const [over, setOver] = useState<string | null>(null);
    const props = (folder: Row) => {
        const drop = common.drop;
        if (!drop) return {};
        return {
            draggable: folder.Level === "MANAGE",
            onDragStart: (event: React.DragEvent) => {
                event.stopPropagation();
                event.dataTransfer.setData("text/plain", folder.USID);
                event.dataTransfer.effectAllowed = "move";
                drop.onFolderDragStart(folder);
            },
            onDragEnd: () => { setOver(null); drop.onDragFinished(); },
            onDragOver: (event: React.DragEvent) => {
                if (!drop.canDrop(folder)) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setOver(folder.USID);
            },
            onDragLeave: (event: React.DragEvent) => {
                if ((event.currentTarget as Node).contains(event.relatedTarget as Node | null)) return;
                setOver(current => current === folder.USID ? null : current);
            },
            onDrop: (event: React.DragEvent) => {
                event.preventDefault();
                setOver(null);
                if (drop.canDrop(folder)) drop.onDrop(folder);
            },
        };
    };
    const lit = (folder: Row) => over === folder.USID ? {outline: `2px dashed ${Blue3}`, outlineOffset: "-2px"} : {};
    const faded = (folder: Row) => common.drop?.folderDrag === folder.USID ? {opacity: 0.5} : {};
    return {props, lit, faded};
}

function dragProps(record: Row, common: Common) {
    return common.mayFile ? {
        draggable: true,
        onDragStart: (event: React.DragEvent) => {
            event.dataTransfer.setData("text/plain", record.ObjectID ?? record.Name ?? "");
            event.dataTransfer.effectAllowed = "move";
            common.onRecordDragStart(record);
        },
        onDragEnd: common.onRecordDragEnd,
    } : {};
}

// ── Icons ──────────────────────────────────────────────────────────────────────────────────────────────────────────

function IconsView({items, ...common}: Common & {items: Item[]}) {
    const target = useFolderTarget(common);
    return (
        <Box sx={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(124px, 1fr))", gap: 1, mt: 1.5}}>
            {items.map(item => {
                const isFolder = item.kind === "folder";
                const Icon = isFolder ? FolderOutlined : recordIcon(item.record);
                const key = isFolder ? item.folder.USID : item.record.ObjectUSID;
                return (
                    <Box key={key} {...(isFolder ? target.props(item.folder) : dragProps(item.record, common))}
                         onClick={() => isFolder ? common.onOpenFolder(item.folder.USID) : common.onOpenRecord(item.record.RevisionUSID)}
                         sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, p: 1.25, borderRadius: "6px",
                              cursor: "pointer", textAlign: "center", "&:hover": {backgroundColor: "#F2F7FB"},
                              ...(isFolder ? {...target.lit(item.folder), ...target.faded(item.folder)} : {})}}>
                        <Icon sx={{fontSize: 44, color: isFolder ? Blue3 : Grey6}}/>
                        {isFolder ? (
                            <Typography sx={{fontSize: 12.5, color: Grey17, lineHeight: 1.3, wordBreak: "break-word"}}>{item.folder.Name}</Typography>
                        ) : <>
                            <Typography sx={{fontSize: 12, color: Blue3, fontWeight: 600}}>{item.record.Doc ? "Parkour Doc" : item.record.ObjectID}</Typography>
                            <Typography sx={{fontSize: 11.5, color: Grey7, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2,
                                             WebkitBoxOrient: "vertical", overflow: "hidden"}}>{item.record.Name}</Typography>
                        </>}
                        {isFolder && item.folder.RecordCount ? <Typography sx={{fontSize: 10.5, color: Grey5}}>{item.folder.RecordCount} records</Typography> : null}
                    </Box>
                );
            })}
        </Box>
    );
}

// ── List ───────────────────────────────────────────────────────────────────────────────────────────────────────────

function ListView({items, recordDragging, ...common}: Common & {items: Item[]; recordDragging?: string | null}) {
    const cell = {fontSize: 13, color: Grey7};
    const target = useFolderTarget(common);
    return (
        <Table size="small" sx={{mt: 1}}>
            <TableHead>
                <TableRow>
                    {["Object ID", "Name", "Type", "Revision", "Status", "Filed by", "Filed"].map(one => (
                        <TableCell key={one} sx={{fontSize: 12, color: Grey5}}>{one}</TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {items.map(item => item.kind === "folder" ? (
                    <TableRow key={item.folder.USID} hover {...target.props(item.folder)}
                              sx={{cursor: "pointer", ...target.lit(item.folder), ...target.faded(item.folder)}} onClick={() => common.onOpenFolder(item.folder.USID)}>
                        <TableCell><FolderOutlined sx={{fontSize: 18, color: Blue3, verticalAlign: "middle"}}/></TableCell>
                        <TableCell sx={{fontSize: 13, color: Grey17}}>
                            {item.folder.Name}
                            {item.folder.InheritGrants === false ? <LockPersonOutlined sx={{fontSize: 14, color: Grey5, ml: 0.75, verticalAlign: "middle"}}/> : null}
                        </TableCell>
                        <TableCell sx={cell}>Folder</TableCell>
                        <TableCell sx={cell}>—</TableCell>
                        <TableCell sx={cell}>{item.folder.RecordCount ? `${item.folder.RecordCount} records` : "—"}</TableCell>
                        <TableCell sx={cell}/>
                        <TableCell sx={cell}/>
                    </TableRow>
                ) : (
                    <TableRow key={item.record.ObjectUSID} hover {...dragProps(item.record, common)}
                              sx={{opacity: recordDragging === item.record.ObjectUSID ? 0.5 : 1}}>
                        <TableCell sx={{whiteSpace: "nowrap"}}>
                            {common.mayFile ? (
                                <Tooltip title="Drag onto a folder in the tree to move it there" placement="left">
                                    <DragIndicatorOutlined sx={{fontSize: 16, color: Grey5, cursor: "grab", verticalAlign: "middle", mr: 0.5, ml: -1}}/>
                                </Tooltip>
                            ) : null}
                            {item.record.Doc ? (
                                <Typography component="span" onClick={() => common.onOpenRecord(item.record.RevisionUSID)}
                                            sx={{fontSize: 13, color: Blue3, cursor: "pointer", "&:hover": {textDecoration: "underline"}}}>
                                    <ArticleOutlined sx={{fontSize: 16, verticalAlign: "middle", mr: 0.5}}/>Open in Docs
                                </Typography>
                            ) : <ObjectIdLink id={item.record.ObjectID} revisionUSID={item.record.RevisionUSID} weight={400}/>}
                        </TableCell>
                        <TableCell sx={{fontSize: 13}}>{item.record.Name}</TableCell>
                        <TableCell sx={cell}>{typeName(item.record)}</TableCell>
                        <TableCell sx={cell}>{item.record.Revision ?? "—"}</TableCell>
                        <TableCell sx={cell}>{item.record.Status ?? "—"}</TableCell>
                        <TableCell sx={cell}>{item.record.FiledBy ?? "Not recorded"}</TableCell>
                        <TableCell sx={cell}>{item.record.FiledDatetime ? new Date(item.record.FiledDatetime).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

// ── The preview Columns and Gallery share ──────────────────────────────────────────────────────────────────────────

function Fact({label, value}: {label: string; value: any}) {
    return (
        <Box sx={{display: "flex", gap: 1.5, py: 0.25}}>
            <Typography sx={{fontSize: 12, color: Grey5, width: 76, flexShrink: 0}}>{label}</Typography>
            <Typography sx={{fontSize: 12.5, color: Grey17}}>{value ?? "—"}</Typography>
        </Box>
    );
}

function Preview({item, big, ...common}: Common & {item: Item; big?: boolean}) {
    if (item.kind === "folder") {
        return (
            <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 1, p: 2, textAlign: "center"}}>
                <FolderOutlined sx={{fontSize: big ? 120 : 72, color: Blue3}}/>
                <Typography sx={{fontSize: big ? 18 : 14, fontWeight: 500, color: Grey17}}>{item.folder.Name}</Typography>
                <Typography sx={{fontSize: 12.5, color: Grey5}}>{item.folder.RecordCount ? `${item.folder.RecordCount} records` : "No records filed here"}</Typography>
                <Button size="small" variant="outlined" sx={{textTransform: "none", mt: 0.5}} onClick={() => common.onOpenFolder(item.folder.USID)}>Open</Button>
            </Box>
        );
    }
    const record = item.record;
    const Icon = recordIcon(record);
    return (
        <Box sx={{display: "flex", flexDirection: big ? "row" : "column", alignItems: big ? "flex-start" : "center", gap: big ? 4 : 1.25,
                  p: 2, justifyContent: "center"}}>
            <Icon sx={{fontSize: big ? 120 : 72, color: Grey6}}/>
            <Box sx={{minWidth: 0, maxWidth: 420}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.5, justifyContent: big ? "flex-start" : "center"}}>
                    {record.Doc ? null : <ObjectIdLink id={record.ObjectID} revisionUSID={record.RevisionUSID} size={big ? 16 : 14}/>}
                </Box>
                <Typography sx={{fontSize: big ? 17 : 13.5, color: Grey17, mb: 1.25, textAlign: big ? "left" : "center"}}>{record.Name}</Typography>
                <Fact label="Type" value={typeName(record)}/>
                <Fact label="Status" value={record.Status}/>
                <Fact label="Revision" value={record.Revision}/>
                <Fact label={record.Doc ? "Created by" : "Filed by"} value={record.FiledBy ?? "Not recorded"}/>
                <Fact label={record.Doc ? "Created" : "Filed"} value={record.FiledDatetime ? new Date(record.FiledDatetime).toLocaleDateString() : null}/>
                <Button size="small" variant="outlined" sx={{textTransform: "none", mt: 1.25}} onClick={() => common.onOpenRecord(record.RevisionUSID)}>
                    {record.Doc ? "Open in Docs" : "Show details, form and files"}
                </Button>
            </Box>
        </Box>
    );
}

function itemKey(item: Item) {
    return item.kind === "folder" ? `f:${item.folder.USID}` : `r:${item.record.ObjectUSID}`;
}

// ── Columns ────────────────────────────────────────────────────────────────────────────────────────────────────────

export type Column = {
    /** The folder this column lists; null for the root. */
    usid: string | null;
    name: string;
    folders: Row[];
    /** Null while an ancestor's records are being read. */
    records: Row[] | null;
    /** The folder in this column that is open further right, highlighted the way Finder does. */
    next?: string | null;
};

/** How wide a column starts, and the narrowest and widest it can be dragged. */
export const COLUMN_WIDTH = 230, MIN_COLUMN = 140, MAX_COLUMN = 640;

/** A column's width after dragging its handle by `dx` pixels, held between the bounds. */
export function resized(width: number, dx: number) {
    return Math.max(MIN_COLUMN, Math.min(MAX_COLUMN, Math.round(width + dx)));
}

/** A COLUMN'S HANDLE, drawn as the Filter by pane's gripper (Ed, 2026-11-03: "place the gripper in the vertical center
 *  and make it look like the gripper used in expanding the filter by section"): a small tab on the column's right edge,
 *  half way down. Drag to resize; double-click to put the column back to its starting width. */
function ColumnHandle({width, onWidth, label}: {width: number; onWidth: (width: number) => void; label: string}) {
    return (
        <Tooltip title="Drag to resize; double-click to reset" placement="right">
            <Box role="separator" aria-orientation="vertical" aria-label={`Resize ${label}`}
                 onDoubleClick={() => onWidth(COLUMN_WIDTH)}
                 onMouseDown={event => {
                     event.preventDefault();
                     const start = event.clientX;
                     const move = (moved: MouseEvent) => onWidth(resized(width, moved.clientX - start));
                     const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); document.body.style.cursor = ""; };
                     document.body.style.cursor = "col-resize";
                     window.addEventListener("mousemove", move);
                     window.addEventListener("mouseup", up);
                 }}
                 sx={{position: "absolute", top: "50%", transform: "translateY(-50%)", right: -10, zIndex: 2,
                      width: 9, height: 34, borderRadius: "0 3px 3px 0", border: "1px solid #E4E7EC", borderLeft: "none",
                      backgroundColor: "#FAFBFC", cursor: "col-resize", display: "flex", alignItems: "center", justifyContent: "center",
                      "&:hover": {backgroundColor: "#EDEFF3"}}}>
                <DragIndicator sx={{fontSize: 12, color: Grey5}}/>
            </Box>
        </Tooltip>
    );
}

function ColumnsView({columns, ...common}: Common & {columns: Column[]}) {
    const [selected, setSelected] = useState<Item | null>(null);
    const target = useFolderTarget(common);
    // WIDTHS BY POSITION, as Finder keeps them: widening the second column keeps it wide as you go deeper.
    const [widths, setWidths] = useState<Record<string, number>>({});
    const widthOf = (key: string) => widths[key] ?? (key === "preview" ? 320 : COLUMN_WIDTH);
    const setWidth = (key: string) => (width: number) => setWidths(current => ({...current, [key]: width}));
    const last = columns[columns.length - 1]?.usid ?? null;
    useEffect(() => { setSelected(null); }, [last]);
    const row = (active: boolean) => ({display: "flex", alignItems: "center", gap: 0.75, px: 1, py: "4px", borderRadius: "4px",
                                       cursor: "pointer", backgroundColor: active ? "#DCEBF5" : "transparent",
                                       "&:hover": {backgroundColor: active ? "#DCEBF5" : "#F2F7FB"}});
    return (
        <Box sx={{display: "flex", mt: 1.5, border: "1px solid #E4E7EC", borderRadius: "6px", height: "calc(100vh - 360px)", minHeight: 320,
                  overflowX: "auto"}}>
            {columns.map((column, index) => (
                <Box key={column.usid ?? "root"} sx={{width: widthOf(String(index)), flexShrink: 0, borderRight: "1px solid #E4E7EC", position: "relative",
                                                      display: "flex", flexDirection: "column"}}>
                    <Box sx={{overflowY: "auto", p: 0.75, flexGrow: 1}}>
                    {column.folders.map(folder => (
                        <Box key={folder.USID} {...target.props(folder)} sx={{...row(column.next === folder.USID), ...target.lit(folder), ...target.faded(folder)}}
                             onClick={() => common.onOpenFolder(folder.USID)}>
                            <FolderOutlined sx={{fontSize: 17, color: Blue3}}/>
                            <Typography sx={{fontSize: 12.5, color: Grey17, flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{folder.Name}</Typography>
                            <ChevronRightOutlined sx={{fontSize: 16, color: Grey5}}/>
                        </Box>
                    ))}
                    {column.records === null ? (
                        <Typography sx={{fontSize: 11.5, color: Grey5, px: 1, py: 0.5}}>Reading…</Typography>
                    ) : column.records.map(record => {
                        const Icon = recordIcon(record);
                        const active = index === columns.length - 1 && selected?.kind === "record" && selected.record.ObjectUSID === record.ObjectUSID;
                        return (
                            <Box key={record.ObjectUSID} sx={row(active)} {...dragProps(record, common)}
                                 onClick={() => index === columns.length - 1 ? setSelected({kind: "record", record}) : common.onOpenRecord(record.RevisionUSID)}>
                                <Icon sx={{fontSize: 17, color: Grey6}}/>
                                <Typography sx={{fontSize: 12.5, color: Grey17, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                    <b style={{fontWeight: 600}}>{record.ObjectID}</b> {record.Name}
                                </Typography>
                            </Box>
                        );
                    })}
                    {!column.folders.length && column.records !== null && !column.records.length ? (
                        <Typography sx={{fontSize: 11.5, color: Grey5, px: 1, py: 0.5}}>Empty</Typography>
                    ) : null}
                    </Box>
                    <ColumnHandle width={widthOf(String(index))} onWidth={setWidth(String(index))} label={column.name}/>
                </Box>
            ))}
            {selected ? (
                <Box sx={{width: widthOf("preview"), flexShrink: 0, overflowY: "auto", position: "relative", borderRight: "1px solid #E4E7EC"}}>
                    <Preview item={selected} {...common}/>
                    <ColumnHandle width={widthOf("preview")} onWidth={setWidth("preview")} label="the preview"/>
                </Box>
            ) : null}
        </Box>
    );
}

// ── Gallery ────────────────────────────────────────────────────────────────────────────────────────────────────────

function GalleryView({items, ...common}: Common & {items: Item[]}) {
    const [at, setAt] = useState(0);
    const target = useFolderTarget(common);
    const keys = items.map(itemKey).join("|");
    useEffect(() => { setAt(0); }, [keys]);
    const current = items[Math.min(at, items.length - 1)];
    return (
        <Box sx={{mt: 1.5}}>
            <Box sx={{minHeight: 300, border: "1px solid #E4E7EC", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center"}}
                 tabIndex={0}
                 onKeyDown={event => {
                     if (event.key === "ArrowRight") setAt(index => Math.min(index + 1, items.length - 1));
                     if (event.key === "ArrowLeft") setAt(index => Math.max(index - 1, 0));
                 }}>
                {current ? <Preview item={current} big {...common}/> : null}
            </Box>
            <Box sx={{display: "flex", gap: 1, overflowX: "auto", py: 1.25, mt: 1}}>
                {items.map((item, index) => {
                    const isFolder = item.kind === "folder";
                    const Icon = isFolder ? FolderOutlined : recordIcon(item.record);
                    return (
                        <Box key={itemKey(item)} onClick={() => setAt(index)} {...(isFolder ? target.props(item.folder) : dragProps(item.record, common))}
                             sx={{width: 96, flexShrink: 0, p: 0.75, borderRadius: "6px", cursor: "pointer", textAlign: "center",
                                  border: index === at ? `2px solid ${Blue3}` : "2px solid transparent", "&:hover": {backgroundColor: "#F2F7FB"},
                                  ...(isFolder ? {...target.lit(item.folder), ...target.faded(item.folder)} : {})}}>
                            <Icon sx={{fontSize: 32, color: isFolder ? Blue3 : Grey6}}/>
                            <Typography sx={{fontSize: 11, color: Grey17, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                {isFolder ? item.folder.Name : item.record.ObjectID}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

export default function FolderContents({view, items, columns, recordDragging, empty, ...common}: Common & {
    view: FolderView; items: Item[]; columns: Column[]; recordDragging?: string | null; empty: string;
}) {
    if (view === "columns") return <ColumnsView columns={columns} {...common}/>;
    if (!items.length) return <Typography sx={{fontSize: 13, color: Grey5, mt: 1.5}}>{empty}</Typography>;
    if (view === "icons") return <IconsView items={items} {...common}/>;
    if (view === "gallery") return <GalleryView items={items} {...common}/>;
    return <ListView items={items} recordDragging={recordDragging} {...common}/>;
}

